import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { InAppBrowserWarning } from '../components/InAppBrowserWarning';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CreditCard, Loader2, Lock, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddCardLinkPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payments/card-setup-link/${token}`);
        setInfo(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'This link is invalid.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleContinue = async () => {
    setRedirecting(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/payments/card-setup-link/${token}/session`);
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start the secure card page. Please try again.');
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-full mb-4">
            <CreditCard className="w-8 h-8 text-[#FF007F]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Add Your Payment Card</h1>
          <p className="text-white/60">Universal Performing Arts</p>
        </div>

        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-6 space-y-5">
            {error ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-300 text-center" data-testid="add-card-error">
                {error}
              </div>
            ) : (
              <>
                <InAppBrowserWarning />
                {info?.parent_name && (
                  <p className="text-white/80 text-center">
                    Hi <span className="font-semibold text-white">{info.parent_name}</span> — click below to securely
                    add your card to your UPA account.
                  </p>
                )}
                <Button
                  onClick={handleContinue}
                  disabled={redirecting}
                  className="w-full bg-[#FF007F] hover:bg-[#D6006A] text-white h-12 text-base"
                  data-testid="add-card-continue"
                >
                  {redirecting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening secure page…</>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" /> Continue to Secure Card Page</>
                  )}
                </Button>
                <div className="flex items-center justify-center gap-2 text-xs text-white/40">
                  <ShieldCheck className="w-4 h-4" />
                  Powered by Stripe — your card details are never stored on our servers
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
